/**
 * AuthContext — httpOnly-cookie-based auth.
 *
 * Token storage moved out of localStorage to mitigate XSS exfiltration.
 * Login endpoints set a `jpd_session` cookie via Set-Cookie headers, and
 * `axios.defaults.withCredentials = true` makes every subsequent request
 * carry it automatically. The browser refuses to expose the cookie value
 * to JavaScript, so there's nothing for a malicious script to read.
 *
 * `isAuthenticated` is derived from a single GET /api/auth/me probe at
 * startup — a 200 means we have a valid cookie, a 401 means we're logged
 * out.
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send cookies on every cross-origin request (same-origin already does this
// by default, but the preview environment can be served on a different host).
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('logout:', e?.message); }
    setUser(null);
    try { if ('clearAppBadge' in navigator) navigator.clearAppBadge(); }
    catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('clearAppBadge:', e?.message); }
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      // 401 = not logged in (expected at first load before login)
      if (process.env.NODE_ENV !== 'production' && e?.response?.status !== 401) {
        console.error('Failed to fetch user:', e);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const _doLogin = useCallback(async (path, email, password) => {
    const r = await axios.post(`${API}${path}`, { email, password });
    // Cookie is set by the backend. We only use the response body for the
    // user identity payload — no token persisted client-side.
    const { user_id, role, name } = r.data;
    setUser({ user_id, role, name });
    return r.data;
  }, []);

  const loginAdmin  = useCallback((email, password) => _doLogin('/auth/admin/login',  email, password), [_doLogin]);
  const loginClient = useCallback((email, password) => _doLogin('/auth/client/login', email, password), [_doLogin]);
  const loginLab    = useCallback((email, password) => _doLogin('/auth/lab/login',    email, password), [_doLogin]);

  /**
   * Refresh the cookie's expiry (sliding 30-day session). The backend also
   * returns a fresh access_token for legacy callers — we don't need it.
   */
  const heartbeat = useCallback(async () => {
    try { await axios.post(`${API}/heartbeat`); }
    catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('heartbeat:', e?.message); }
  }, []);

  // Memoise the context value so consumers don't re-render on every parent
  // re-render. Identity functions like isAdmin/isClient/isLab are recomputed
  // only when `user` actually changes.
  const value = useMemo(() => ({
    user, loading,
    loginAdmin, loginClient, loginLab,
    logout, heartbeat,
    isAdmin:  () => user?.role === 'admin',
    isClient: () => user?.role === 'client',
    isLab:    () => user?.role === 'lab',
    isAuthenticated: !!user,
  }), [user, loading, loginAdmin, loginClient, loginLab, logout, heartbeat]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
